import React, { useState, useEffect } from 'react';
import { Download, RefreshCw, Mail, Phone, Calendar, Package, DollarSign, User, Search, Filter, Shield, Bell } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import axios from 'axios';
import { useToast } from '../hooks/use-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

const Admin = () => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const [quotes, setQuotes] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [authenticated, setAuthenticated] = useState(false);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loggingIn, setLoggingIn] = useState(false);
  const [token, setToken] = useState(localStorage.getItem('admin_token') || '');
  const [pushEnabled, setPushEnabled] = useState(false);

  useEffect(() => {
    // Check if already logged in with valid token
    if (token) {
      setAuthenticated(true);
      fetchAllData();
    }
    // Check if push notifications are supported and enabled
    if ('Notification' in window && Notification.permission === 'granted') {
      setPushEnabled(true);
    }
  }, []);

  const enablePushNotifications = async () => {
    try {
      // Request permission
      const permission = await Notification.requestPermission();
      if (permission !== 'granted') {
        toast({ title: 'Permission Denied', description: 'Please allow notifications in your browser settings', variant: 'destructive' });
        return;
      }

      // Register service worker
      const registration = await navigator.serviceWorker.register('/sw.js');
      
      // Get VAPID public key
      const vapidResponse = await axios.get(`${API}/vapid-public-key`);
      const vapidPublicKey = vapidResponse.data.publicKey;
      
      // Convert VAPID key
      const urlBase64ToUint8Array = (base64String) => {
        const padding = '='.repeat((4 - base64String.length % 4) % 4);
        const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
        const rawData = window.atob(base64);
        const outputArray = new Uint8Array(rawData.length);
        for (let i = 0; i < rawData.length; ++i) {
          outputArray[i] = rawData.charCodeAt(i);
        }
        return outputArray;
      };

      // Subscribe to push
      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(vapidPublicKey)
      });

      // Send subscription to backend
      await axios.post(`${API}/push-subscribe`, subscription.toJSON());
      
      setPushEnabled(true);
      toast({ title: '🔔 Notifications Enabled!', description: 'You will receive alerts when visitors land on your site.' });
    } catch (error) {
      console.error('Push subscription error:', error);
      toast({ title: 'Error', description: 'Failed to enable notifications', variant: 'destructive' });
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoggingIn(true);
    
    try {
      const response = await axios.post(`${API}/admin/login`, {
        username,
        password
      });
      
      const { access_token } = response.data;
      
      // Store token
      localStorage.setItem('admin_token', access_token);
      setToken(access_token);
      setAuthenticated(true);
      
      toast({
        title: "Access Granted",
        description: "Welcome to your admin dashboard",
      });
      
      // Fetch data after successful login
      fetchAllData();
    } catch (error) {
      console.error('Login error:', error);
      toast({
        title: "Access Denied",
        description: "Incorrect username or password",
        variant: "destructive"
      });
    } finally {
      setLoggingIn(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    setToken('');
    setAuthenticated(false);
    setUsername('');
    setPassword('');
    toast({
      title: "Logged Out",
      description: "You have been logged out successfully",
    });
  };

  const fetchAllData = async () => {
    setLoading(true);
    try {
      const authToken = localStorage.getItem('admin_token');
      const headers = {
        'Authorization': `Bearer ${authToken}`
      };
      
      const [quotesRes, contactsRes, bookingsRes] = await Promise.all([
        axios.get(`${API}/quotes`, { headers }),
        axios.get(`${API}/contacts`, { headers }),
        axios.get(`${API}/bookings`, { headers })
      ]);
      
      setQuotes(quotesRes.data);
      setContacts(contactsRes.data);
      setBookings(bookingsRes.data);
      
      toast({
        title: "Data Loaded",
        description: `${quotesRes.data.length} quotes, ${contactsRes.data.length} contacts, ${bookingsRes.data.length} bookings`,
      });
    } catch (error) {
      console.error('Error fetching data:', error);
      
      // If unauthorized, clear token and logout
      if (error.response && error.response.status === 401) {
        handleLogout();
        toast({
          title: "Session Expired",
          description: "Please login again",
          variant: "destructive"
        });
      } else {
        toast({
          title: "Error",
          description: "Failed to load data",
          variant: "destructive"
        });
      }
    }
    setLoading(false);
  };

  const exportToCSV = (data, filename) => {
    if (!data || data.length === 0) {
      toast({
        title: "No Data",
        description: "No data to export",
        variant: "destructive"
      });
      return;
    }

    const headers = Object.keys(data[0]).join(',');
    const rows = data.map(item => 
      Object.values(item).map(val => 
        typeof val === 'string' && val.includes(',') ? `"${val}"` : val
      ).join(',')
    );
    
    const csv = [headers, ...rows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${filename}_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    
    toast({
      title: "Export Successful",
      description: `${filename}.csv downloaded`,
    });
  };

  if (!authenticated) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle className="text-2xl text-center">Admin Access</CardTitle>
            <CardDescription className="text-center">Secure login to view submissions</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleLogin} className="space-y-4">
              <div>
                <Input
                  type="text"
                  placeholder="Username"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  className="text-lg"
                  required
                  autoComplete="username"
                />
              </div>
              <div>
                <Input
                  type="password"
                  placeholder="Password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="text-lg"
                  required
                  autoComplete="current-password"
                />
              </div>
              <Button 
                type="submit" 
                className="w-full bg-emerald-700 hover:bg-emerald-800"
                disabled={loggingIn}
              >
                {loggingIn ? 'Logging in...' : 'Login'}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-4 md:p-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-3xl font-bold text-black">MySelfie Moments</h1>
              <p className="text-gray-600">Admin Dashboard</p>
            </div>
            <div className="flex gap-2">
              <Button 
                onClick={enablePushNotifications}
                disabled={pushEnabled}
                variant="outline"
                className={pushEnabled ? "border-green-600 text-green-600" : "border-amber-600 text-amber-600 hover:bg-amber-50"}
              >
                <Bell className="mr-2 h-4 w-4" />
                {pushEnabled ? 'Notifications On' : 'Enable Alerts'}
              </Button>
              <Button 
                onClick={() => navigate('/admin/security')}
                variant="outline"
                className="border-emerald-700 text-emerald-700 hover:bg-emerald-50"
              >
                <Shield className="mr-2 h-4 w-4" />
                Security
              </Button>
              <Button 
                onClick={fetchAllData} 
                disabled={loading}
                className="bg-emerald-700 hover:bg-emerald-800"
              >
                <RefreshCw className={`mr-2 h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
              <Button 
                onClick={handleLogout}
                variant="outline"
                className="border-red-600 text-red-600 hover:bg-red-50"
              >
                Logout
              </Button>
            </div>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600">Quote Requests</p>
                    <p className="text-3xl font-bold text-black">{quotes.length}</p>
                  </div>
                  <Package className="h-8 w-8 text-emerald-700" />
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600">Contact Forms</p>
                    <p className="text-3xl font-bold text-black">{contacts.length}</p>
                  </div>
                  <Mail className="h-8 w-8 text-emerald-700" />
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600">Booking Inquiries</p>
                    <p className="text-3xl font-bold text-black">{bookings.length}</p>
                  </div>
                  <Calendar className="h-8 w-8 text-emerald-700" />
                </div>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Search */}
        <div className="mb-6">
          <div className="relative">
            <Search className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
            <Input
              placeholder="Search by name, email, phone..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
        </div>

        {/* Tabs */}
        <Tabs defaultValue="quotes" className="space-y-4">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="quotes">Quotes ({quotes.length})</TabsTrigger>
            <TabsTrigger value="contacts">Contacts ({contacts.length})</TabsTrigger>
            <TabsTrigger value="bookings">Bookings ({bookings.length})</TabsTrigger>
          </TabsList>

          {/* Quotes Tab */}
          <TabsContent value="quotes" className="space-y-4">
            <div className="flex justify-between items-center">
              <h2 className="text-xl font-semibold">Quote Requests</h2>
              <Button 
                onClick={() => exportToCSV(quotes, 'quotes')}
                variant="outline"
              >
                <Download className="mr-2 h-4 w-4" />
                Export CSV
              </Button>
            </div>
            
            <div className="space-y-4">
              {quotes.filter(q => 
                searchTerm === '' || 
                q.customerName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                q.customerEmail?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                q.customerPhone?.includes(searchTerm)
              ).map((quote) => (
                <Card key={quote.id}>
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-lg">{quote.customerName}</CardTitle>
                      <Badge className="bg-emerald-700">£{quote.totalPrice}</Badge>
                    </div>
                    <CardDescription>
                      {new Date(quote.createdAt).toLocaleDateString('en-GB', { 
                        day: 'numeric',
                        month: 'long', 
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <p className="text-sm text-gray-600 mb-2"><strong>Contact:</strong></p>
                        <p className="text-sm flex items-center gap-2 mb-1">
                          <Mail className="h-4 w-4 text-emerald-700" />
                          <a href={`mailto:${quote.customerEmail}`} className="text-emerald-700 hover:underline">
                            {quote.customerEmail}
                          </a>
                        </p>
                        <p className="text-sm flex items-center gap-2">
                          <Phone className="h-4 w-4 text-emerald-700" />
                          <a href={`tel:${quote.customerPhone}`} className="text-emerald-700 hover:underline">
                            {quote.customerPhone}
                          </a>
                        </p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-600 mb-2"><strong>Event Details:</strong></p>
                        <p className="text-sm"><strong>Package:</strong> {quote.package}</p>
                        <p className="text-sm"><strong>Date:</strong> {quote.eventDate}</p>
                        <p className="text-sm"><strong>Time:</strong> {quote.eventTime}</p>
                        {quote.eventType && <p className="text-sm"><strong>Type:</strong> {quote.eventType}</p>}
                        {quote.guestCount && <p className="text-sm"><strong>Guests:</strong> {quote.guestCount}</p>}
                      </div>
                    </div>
                    {quote.addOns && quote.addOns.length > 0 && (
                      <div className="mt-4">
                        <p className="text-sm text-gray-600 mb-2"><strong>Add-ons:</strong></p>
                        <div className="flex flex-wrap gap-2">
                          {quote.addOns.map((addon, idx) => (
                            <Badge key={idx} variant="outline">{addon}</Badge>
                          ))}
                        </div>
                      </div>
                    )}
                    {quote.extraHours > 0 && (
                      <p className="text-sm mt-2"><strong>Extra Hours:</strong> {quote.extraHours}</p>
                    )}
                    {quote.specialRequests && (
                      <div className="mt-4 p-3 bg-gray-50 rounded">
                        <p className="text-sm text-gray-600 mb-1"><strong>Special Requests:</strong></p>
                        <p className="text-sm">{quote.specialRequests}</p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}
              {quotes.length === 0 && (
                <Card>
                  <CardContent className="pt-6 text-center text-gray-500">
                    No quote requests yet
                  </CardContent>
                </Card>
              )}
            </div>
          </TabsContent>

          {/* Contacts Tab */}
          <TabsContent value="contacts" className="space-y-4">
            <div className="flex justify-between items-center">
              <h2 className="text-xl font-semibold">Contact Submissions</h2>
              <Button 
                onClick={() => exportToCSV(contacts, 'contacts')}
                variant="outline"
              >
                <Download className="mr-2 h-4 w-4" />
                Export CSV
              </Button>
            </div>
            
            <div className="space-y-4">
              {contacts.filter(c => 
                searchTerm === '' || 
                c.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                c.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                c.phone?.includes(searchTerm)
              ).map((contact) => (
                <Card key={contact.id}>
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-lg">{contact.name}</CardTitle>
                      {contact.eventType && <Badge>{contact.eventType}</Badge>}
                    </div>
                    <CardDescription>
                      {new Date(contact.createdAt).toLocaleDateString('en-GB', { 
                        day: 'numeric',
                        month: 'long', 
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      <div className="flex items-center gap-2">
                        <Mail className="h-4 w-4 text-emerald-700" />
                        <a href={`mailto:${contact.email}`} className="text-emerald-700 hover:underline">
                          {contact.email}
                        </a>
                      </div>
                      <div className="flex items-center gap-2">
                        <Phone className="h-4 w-4 text-emerald-700" />
                        <a href={`tel:${contact.phone}`} className="text-emerald-700 hover:underline">
                          {contact.phone}
                        </a>
                      </div>
                      {contact.eventDate && (
                        <p className="text-sm"><strong>Event Date:</strong> {contact.eventDate}</p>
                      )}
                      {contact.location && (
                        <p className="text-sm"><strong>Location:</strong> {contact.location}</p>
                      )}
                      <div className="mt-4 p-3 bg-gray-50 rounded">
                        <p className="text-sm text-gray-600 mb-1"><strong>Message:</strong></p>
                        <p className="text-sm whitespace-pre-wrap">{contact.message}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
              {contacts.length === 0 && (
                <Card>
                  <CardContent className="pt-6 text-center text-gray-500">
                    No contact submissions yet
                  </CardContent>
                </Card>
              )}
            </div>
          </TabsContent>

          {/* Bookings Tab */}
          <TabsContent value="bookings" className="space-y-4">
            <div className="flex justify-between items-center">
              <h2 className="text-xl font-semibold">Booking Inquiries</h2>
              <Button 
                onClick={() => exportToCSV(bookings, 'bookings')}
                variant="outline"
              >
                <Download className="mr-2 h-4 w-4" />
                Export CSV
              </Button>
            </div>
            
            <div className="space-y-4">
              {bookings.map((booking) => (
                <Card key={booking.id}>
                  <CardContent className="pt-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-lg font-semibold">{booking.selectedDate}</p>
                        <p className="text-sm text-gray-600">
                          {new Date(booking.createdAt).toLocaleDateString('en-GB', { 
                            day: 'numeric',
                            month: 'long', 
                            year: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </p>
                      </div>
                      <Badge className={booking.isBooked ? 'bg-amber-600' : 'bg-emerald-700'}>
                        {booking.isBooked ? 'Date was booked' : 'Date was available'}
                      </Badge>
                    </div>
                  </CardContent>
                </Card>
              ))}
              {bookings.length === 0 && (
                <Card>
                  <CardContent className="pt-6 text-center text-gray-500">
                    No booking inquiries yet
                  </CardContent>
                </Card>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default Admin;
